import PropTypes from "prop-types";
import React, { useCallback, useMemo } from "react";
import { imageResizer } from "../../lib/utils";
import { ErrorHandler } from "./ErrorHandler";
import { PromoState } from "../../globals";
import { useVideoDataContext } from "../contexts/VideoDataContext";
import { useStickyVideoContext } from "../contexts/StickyVideoContext";
import { useOrcaCommonContext } from "../contexts/OrcaCommonContext";

export const useCalcCss = ({
  isOpenSticky,
  aspectRatio,
  aspectMatch,
  blur,
  color
}) => {
  const calcCss = useCallback(() => {
    const commonStyles = {
      width: "100%",
      position: "relative",
      backgroundPosition: "center",
      backgroundSize: aspectMatch ? "cover" : "contain",
      paddingBottom: `calc(${aspectRatio}*100%)`
    };

    const stickyStyles = {
      ...commonStyles,
      backgroundColor: "unset",
      backgroundImage: "none"
    };

    const nonStickyStyles = {
      ...commonStyles,
      backgroundRepeat: "no-repeat",
      backgroundColor: color || "black",
      ...(blur
        ? {
            filter: "blur(5px)",
            transition: "filter .1s"
          }
        : {})
    };

    return isOpenSticky ? stickyStyles : nonStickyStyles;
  }, [isOpenSticky, aspectRatio, aspectMatch, blur, color]);

  return calcCss;
};

export const useBackgroundImage = ({
  placeholderImage,
  resize,
  videoData,
  originalVideo
}) => {
  const getImageWidth = useCallback(() => {
    // NOTE: May require sync up with the spectrum code
    // see https://github.com/WashPost/spectrum/blob/main/components/document-elements/shared/lede-art-preload/index.jsx#L54
    return videoData?.additional_properties?.vertical ? 480 : 960;
  }, [videoData?.additional_properties?.vertical]);

  const isFirstVideo = (videoData, originalVideo) => {
    return !originalVideo?._id || videoData?._id === originalVideo?._id;
  };

  return useCallback(() => {
    const imageWidth = getImageWidth(videoData);

    if (placeholderImage && isFirstVideo(videoData, originalVideo)) {
      return resize
        ? imageResizer(placeholderImage, imageWidth)
        : placeholderImage;
    }

    const videoImage = videoData?.bandito?.image || videoData?.promo_image?.url;
    return videoImage ? imageResizer(videoImage, imageWidth) : null;
  }, [placeholderImage, resize, videoData, originalVideo, getImageWidth]);
};

export const Wrapper = ({
  aspectRatio,
  aspectMatch,
  placeholder,
  className,
  caption,
  children,
  promoState
}) => {
  const { originalVideo, videoData, videoApiDataFailed } =
    useVideoDataContext();
  const { isOpenSticky } = useStickyVideoContext();
  const { playerState } = useOrcaCommonContext();

  const { image: placeholderImage, resize, blur, color } = placeholder || {};

  const bgImageUrl = useBackgroundImage({
    placeholderImage,
    resize,
    videoData,
    originalVideo
  })();

  const shouldShowBackgroundImage = useMemo(() => {
    return (
      bgImageUrl && (promoState !== PromoState.HIDDEN || !playerState.isStarted)
    );
  }, [bgImageUrl, promoState, playerState]);

  const calcCss = useCalcCss({
    isOpenSticky,
    aspectRatio,
    aspectMatch,
    blur,
    color,
    promoState,
    playerState
  });

  return (
    <figure className={className} style={{ margin: 0 }}>
      <div
        data-testid="orca-wrapper"
        style={{
          backgroundImage: shouldShowBackgroundImage
            ? `url(${bgImageUrl})`
            : "none",
          ...calcCss()
        }}
      >
        {videoApiDataFailed && <ErrorHandler />}
        {videoData && children}
      </div>
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
};

Wrapper.propTypes = {
  /** Aspect ratio float, e.g. 0.5625 for 16:9 */
  aspectRatio: PropTypes.number,
  /** Player & stream aspect ratios match */
  aspectMatch: PropTypes.bool,
  /** Placeholder object */
  placeholder: PropTypes.object,
  /** React prop for class */
  className: PropTypes.string,
  /** Video caption */
  caption: PropTypes.node,
  children: PropTypes.node,
  videoApiDataFailed: PropTypes.bool,
  videoID: PropTypes.string,
  isStickyEnabled: PropTypes.bool,
  isOpenSticky: PropTypes.bool,
  promoState: PropTypes.string,
  playerState: PropTypes.object
};
